<div class="nba-card mat-elevation-z2">

  <!-- Header -->
  <div class="nba-header">
    <div class="nba-header-left">
      <mat-icon class="nba-header-icon animate-pulse">auto_awesome</mat-icon>
      <span class="nba-title">CRM Sales Strategist AI</span>
    </div>
    
    <div class="nba-header-right">
      <button mat-icon-button
              matTooltip="Refresh recommendation"
              [disabled]="isRefreshing"
              (click)="refresh()">
        <mat-icon [class.spinning]="isRefreshing">refresh</mat-icon>
      </button>
    </div>
  </div>

  <!-- Loading skeleton -->
  <ng-container *ngIf="isLoading">
    <div class="nba-skeleton">
      <div class="skeleton-line w60"></div>
      <div class="skeleton-line w90"></div>
      <div class="skeleton-line w40"></div>
    </div>
  </ng-container>

  <!-- Refreshing overlay -->
  <ng-container *ngIf="isRefreshing && !isLoading">
    <div class="nba-loading-overlay">
      <mat-spinner diameter="28"></mat-spinner>
      <span>Analysing behavioral signals and revenue opportunities…</span>
    </div>
  </ng-container>

  <!-- Recommendation content -->
  <ng-container *ngIf="recommendation && !isLoading && !isRefreshing">

    <!-- Metadata Badges Row -->
    <div class="nba-meta-badges">
      <span class="nba-badge stage-badge" *ngIf="recommendation.leadStage">
        <mat-icon>trending_up</mat-icon> {{ recommendation.leadStage }}
      </span>
      <span class="nba-badge score-badge" 
            [ngClass]="'score-' + (recommendation.leadScoreCategory?.toLowerCase() || 'warm')">
        <mat-icon>local_fire_department</mat-icon> {{ recommendation.leadScoreCategory || 'WARM' }} LEAD
      </span>
      <span class="nba-badge automation-badge animate-pulse" *ngIf="recommendation.automationSuggested">
        <mat-icon>smart_toy</mat-icon> AUTOMATION SUGGESTED
      </span>
      <span class="nba-badge review-badge" *ngIf="recommendation.nextReviewDays">
        <mat-icon>schedule</mat-icon> REVIEW IN {{ recommendation.nextReviewDays }} {{ recommendation.nextReviewDays === 1 ? 'DAY' : 'DAYS' }}
      </span>
    </div>

    <!-- Main Recommended Action Card -->
    <div class="nba-action-row" [ngClass]="actionColorClass">
      <div class="nba-action-main">
        <mat-icon class="nba-action-icon">{{ actionIcon }}</mat-icon>
        <span class="nba-action-type">{{ recommendation.recommendedAction || recommendation.actionType }}</span>
      </div>
      <span class="nba-confidence-badge" [ngClass]="confidenceClass">
        <span class="conf-dot"></span>
        {{ confidenceLabel }} CONFIDENCE
      </span>
    </div>

    <!-- High-Impact Explanation Summary -->
    <h3 class="nba-summary-text" *ngIf="recommendation.explanationSummary || recommendation.actionText">
      {{ recommendation.explanationSummary || recommendation.actionText }}
    </h3>

    <!-- Strategic Reasoning Section -->
    <div class="nba-reasoning-section">
      <h4 class="section-title"><mat-icon>psychology</mat-icon> Strategic Reasoning</h4>
      <p class="nba-explanation">
        {{ recommendation.reasoning || recommendation.explanation }}
      </p>
    </div>

    <!-- Operational Execution Roadmap -->
    <div class="nba-execution-box animate-fade-in" *ngIf="recommendation.executionPlan && recommendation.executionPlan.length > 0">
      <h4 class="section-title"><mat-icon>list_alt</mat-icon> Operational Execution Roadmap</h4>
      
      <!-- Recommended Template Highlight -->
      <div class="nba-template-highlight animate-fade-in" *ngIf="recommendation.recommendedTemplate">
        <mat-icon class="template-icon">assignment</mat-icon>
        <div class="template-details">
          <span class="template-label">Recommended Asset / Template</span>
          <span class="template-name">{{ recommendation.recommendedTemplate }}</span>
        </div>
      </div>

      <div class="execution-timeline">
        <div class="timeline-item" *ngFor="let step of recommendation.executionPlan">
          <div class="step-circle">
            <span class="step-number">{{ step.step }}</span>
          </div>
          <div class="step-body">
            <p class="step-action">{{ step.action }}</p>
          </div>
        </div>
      </div>

      <!-- Automation Suggested Banner -->
      <div class="nba-automation-banner animate-fade-in" *ngIf="recommendation.automationSuggested">
        <div class="automation-header">
          <mat-icon class="lightning-icon">bolt</mat-icon>
          <span class="automation-label">AUTOMATION TIP</span>
        </div>
        <p class="automation-desc">
          This sequence is fully compatible with our CRM workflow automation. Add the lead to the respective sequence to trigger WhatsApp and email auto-reminders.
        </p>
      </div>
    </div>

    <!-- Behavioral Signals Grid -->
    <div class="nba-signals-box" *ngIf="recommendation.keySignals && recommendation.keySignals.length > 0">
      <h4 class="section-title"><mat-icon>check_circle_outline</mat-icon> Triggering Signals</h4>
      <div class="signals-grid">
        <div class="signal-item" *ngFor="let signal of recommendation.keySignals">
          <mat-icon class="signal-icon">done</mat-icon>
          <span class="signal-text">{{ signal }}</span>
        </div>
      </div>
    </div>

    <!-- Risk Factors Grid -->
    <div class="nba-risks-box" *ngIf="recommendation.riskFactors && recommendation.riskFactors.length > 0">
      <h4 class="section-title warning-title"><mat-icon>report_problem</mat-icon> Active Risk Factors</h4>
      <div class="risks-grid">
        <div class="risk-item" *ngFor="let risk of recommendation.riskFactors">
          <mat-icon class="risk-icon">warning</mat-icon>
          <span class="risk-text">{{ risk }}</span>
        </div>
      </div>
    </div>

    <!-- Alternative Action Highlight Banner -->
    <div class="nba-alternative-banner" *ngIf="recommendation.alternativeAction">
      <div class="alternative-header">
        <mat-icon>shuffle</mat-icon>
        <span class="alternative-label">ALTERNATIVE STRATEGY</span>
      </div>
      <div class="alternative-value">
        {{ recommendation.alternativeAction }}
      </div>
    </div>

    <!-- Interactive Feedback Row -->
    <div class="nba-feedback-row" *ngIf="!feedbackSubmitted">
      <span class="nba-feedback-label">Log Outcome:</span>
      <div class="feedback-buttons">
        <button mat-flat-button color="primary" class="nba-btn btn-accept"
                (click)="submitFeedback('ACCEPTED')"
                matTooltip="Accept and plan to execute this action">
          <mat-icon>thumb_up</mat-icon> Accept Action
        </button>
        <button mat-stroked-button class="nba-btn btn-done"
                (click)="submitFeedback('EXECUTED')"
                matTooltip="Mark this action as already completed">
          <mat-icon>check_circle</mat-icon> Already Executed
        </button>
        <button mat-button color="warn" class="nba-btn btn-ignore"
                (click)="submitFeedback('IGNORED')"
                matTooltip="Decline or ignore this recommendation">
          <mat-icon>thumb_down</mat-icon> Skip
        </button>
      </div>
    </div>

    <!-- Feedback Complete Message -->
    <div class="nba-feedback-thanks animate-fade-in" *ngIf="feedbackSubmitted">
      <mat-icon>check_circle</mat-icon> Outcome logged. AI strategist model updated.
    </div>

    <!-- Footer metadata -->
    <div class="nba-footer">
      <span>Generated {{ recommendation.generatedAt | date:'dd MMM, HH:mm' }}</span>
      <span class="nba-source" *ngIf="recommendation.source">via {{ recommendation.source }}</span>
    </div>

  </ng-container>

  <!-- Empty state -->
  <ng-container *ngIf="!recommendation && !isLoading && !isRefreshing && !errorMsg">
    <div class="nba-empty">
      <mat-icon class="pulse-icon">insights</mat-icon>
      <p>No active recommendation strategy computed yet.</p>
      <button mat-flat-button color="primary" (click)="refresh()">Compute Recommendation</button>
    </div>
  </ng-container>

  <!-- Error state -->
  <ng-container *ngIf="errorMsg && !isRefreshing">
    <div class="nba-error">
      <mat-icon color="warn">error_outline</mat-icon>
      <p>{{ errorMsg }}</p>
      <button mat-stroked-button (click)="refresh()">Retry Connection</button>
    </div>
  </ng-container>

</div>
